<template>
  <div class="trip-map">
    <GmapMap
      :center="mapCenter"
      :zoom="13"
      :options="mapOptions"
      style="width: 100%; height: 400px"
      ref="gmap"
      @idle="onMapReady"
    >
      <!-- Pickup Location Marker (Start) -->
      <GmapMarker
        v-if="startLocation && startLocation.lat && startLocation.lng"
        :position="{ lat: startLocation.lat, lng: startLocation.lng }"
        :title="'Pickup Location'"
        :icon="pickupIcon"
      />

      <!-- Dropoff Location Marker (End) -->
      <GmapMarker
        v-if="endLocation && endLocation.lat && endLocation.lng"
        :position="{ lat: endLocation.lat, lng: endLocation.lng }"
        :title="'Dropoff Location'"
        :icon="dropoffIcon"
      />

      <!-- Current Driver Location Marker -->
      <GmapMarker
        v-if="currentLocation && currentLocation.lat && currentLocation.lng"
        :position="{ lat: currentLocation.lat, lng: currentLocation.lng }"
        :title="'Your Current Location'"
        :icon="driverIcon"
      />
    </GmapMap>
  </div>
</template>

<script>
export default {
  name: 'TripMap',

  props: {
    startLocation: {
      type: Object,
      default: null,
      // Expected: { lat, lng, address }
    },
    endLocation: {
      type: Object,
      default: null,
      // Expected: { lat, lng, address }
    },
    tripStatus: {
      type: String,
      default: 'ASSIGNED'
    },
    showRouteToEnd: {
      type: Boolean,
      default: false
      // When true, shows route from pickup to dropoff (during delivery)
    },
    showRouteToStart: {
      type: Boolean,
      default: false
      // When true, shows route from current location to pickup
    }
  },

  data() {
    return {
      currentLocation: null,
      locationAccuracy: null,
      lastUpdate: null,
      watchId: null,
      directionsService: null,
      directionsRenderer: null,
      mapReady: false,
      mapOptions: {
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
        disableDefaultUi: false,
      },
      pickupIcon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
            <path fill="#4CAF50" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
        `)}`,
        scaledSize: { width: 40, height: 40 }
      },
      dropoffIcon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
            <path fill="#F44336" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
        `)}`,
        scaledSize: { width: 40, height: 40 }
      },
      driverIcon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
            <path fill="#2196F3" d="M18 18.5a1.5 1.5 0 0 1-1.5-1.5a1.5 1.5 0 0 1 1.5-1.5a1.5 1.5 0 0 1 1.5 1.5a1.5 1.5 0 0 1-1.5 1.5m1.5-9l1.96 2.5H17V9.5M6 18.5A1.5 1.5 0 0 1 4.5 17A1.5 1.5 0 0 1 6 15.5A1.5 1.5 0 0 1 7.5 17A1.5 1.5 0 0 1 6 18.5M20 8h-3V4H3c-1.11 0-2 .89-2 2v11h2a3 3 0 0 0 3 3a3 3 0 0 0 3-3h6a3 3 0 0 0 3 3a3 3 0 0 0 3-3h2v-5z"/>
          </svg>
        `)}`,
        scaledSize: { width: 40, height: 40 }
      }
    };
  },

  computed: {
    mapCenter() {
      // Center on current location if available, otherwise start location
      if (this.currentLocation && this.currentLocation.lat && this.currentLocation.lng) {
        return { lat: this.currentLocation.lat, lng: this.currentLocation.lng };
      }
      if (this.startLocation && this.startLocation.lat && this.startLocation.lng) {
        return { lat: this.startLocation.lat, lng: this.startLocation.lng };
      }
      if (this.endLocation && this.endLocation.lat && this.endLocation.lng) {
        return { lat: this.endLocation.lat, lng: this.endLocation.lng };
      }
      // Default center (Dubai)
      return { lat: 25.2048, lng: 55.2708 };
    }
  },

  watch: {
    showRouteToEnd(newVal) {
      if (newVal && this.mapReady) {
        this.drawRouteToDestination();
      } else if (!newVal && !this.showRouteToStart && this.directionsRenderer) {
        this.clearRoute();
      }
    },
    showRouteToStart(newVal) {
      if (newVal && this.mapReady) {
        this.drawRouteToPickup();
      } else if (!newVal && !this.showRouteToEnd && this.directionsRenderer) {
        this.clearRoute();
      }
    },
    currentLocation() {
      // Update route when driver location changes (for route to pickup)
      if (this.showRouteToStart && this.mapReady && this.currentLocation) {
        this.drawRouteToPickup();
      }
    }
  },

  mounted() {
    this.startLocationTracking();
  },

  beforeDestroy() {
    this.stopLocationTracking();
    this.clearRoute();
  },

  methods: {
    onMapReady() {
      this.mapReady = true;
      
      // Wait for both map and Google Maps API to be ready
      Promise.all([
        this.$refs.gmap.$mapPromise,
        this.$gmapApiPromiseLazy()
      ]).then(([map, google]) => {
        // Initialize Google Maps services
        this.directionsService = new google.maps.DirectionsService();
        this.directionsRenderer = new google.maps.DirectionsRenderer({
          map,
          suppressMarkers: true, // We use custom markers
          polylineOptions: {
            strokeColor: '#2563EB',
            strokeWeight: 5,
            strokeOpacity: 0.8
          }
        });

        // Draw route if needed
        if (this.showRouteToEnd) {
          this.drawRouteToDestination();
        } else if (this.showRouteToStart) {
          this.drawRouteToPickup();
        }
      });
    },

    drawRouteToPickup() {
      if (!this.directionsService || !this.currentLocation || !this.startLocation) {
        return;
      }

      const origin = {
        lat: this.currentLocation.lat,
        lng: this.currentLocation.lng
      };

      const destination = {
        lat: this.startLocation.lat,
        lng: this.startLocation.lng
      };

      this.directionsService.route(
        {
          origin,
          destination,
          travelMode: 'DRIVING'
        },
        (response, status) => {
          if (status === 'OK') {
            this.directionsRenderer.setDirections(response);
            
            // Emit route info (distance and duration to pickup)
            const route = response.routes[0];
            if (route && route.legs && route.legs.length > 0) {
              const leg = route.legs[0];
              this.$emit('route-info', {
                distance: leg.distance.value, // in meters
                duration: leg.duration.value, // in seconds
                distanceText: leg.distance.text,
                durationText: leg.duration.text
              });
            }
          } else {
            // Handle Directions API errors
            let errorMessage = 'Unable to calculate route';
            if (status === 'REQUEST_DENIED') {
              errorMessage = 'Directions API not enabled. Please enable it in Google Cloud Console.';
            } else if (status === 'ZERO_RESULTS') {
              errorMessage = 'No route found between locations';
            }
            this.$emit('route-error', { status, message: errorMessage });
          }
        }
      );
    },

    drawRouteToDestination() {
      if (!this.directionsService || !this.startLocation || !this.endLocation) {
        return;
      }

      const origin = {
        lat: this.startLocation.lat,
        lng: this.startLocation.lng
      };

      const destination = {
        lat: this.endLocation.lat,
        lng: this.endLocation.lng
      };

      this.directionsService.route(
        {
          origin,
          destination,
          travelMode: 'DRIVING'
        },
        (response, status) => {
          if (status === 'OK') {
            this.directionsRenderer.setDirections(response);
            
            // Emit route info (distance and duration)
            const route = response.routes[0];
            if (route && route.legs && route.legs.length > 0) {
              const leg = route.legs[0];
              this.$emit('route-info', {
                distance: leg.distance.value, // in meters
                duration: leg.duration.value, // in seconds
                distanceText: leg.distance.text,
                durationText: leg.duration.text
              });
            }
          } else {
            // Handle Directions API errors
            let errorMessage = 'Unable to calculate route';
            if (status === 'REQUEST_DENIED') {
              errorMessage = 'Directions API not enabled. Please enable it in Google Cloud Console.';
            } else if (status === 'ZERO_RESULTS') {
              errorMessage = 'No route found between locations';
            }
            this.$emit('route-error', { status, message: errorMessage });
          }
        }
      );
    },

    clearRoute() {
      if (this.directionsRenderer) {
        this.directionsRenderer.setDirections({ routes: [] });
      }
    },

    startLocationTracking() {
      if (navigator.geolocation) {
        const geoOptions = {
          enableHighAccuracy: true, // Use GPS instead of IP-based location
          maximumAge: 0, // Don't use cached location, get fresh coordinates
          timeout: 10000 // Wait up to 10 seconds for GPS
        };

        // Get current position immediately with high accuracy
        navigator.geolocation.getCurrentPosition(
          (position) => {
            
            this.currentLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            this.locationAccuracy = Math.round(position.coords.accuracy);
            this.lastUpdate = new Date().toLocaleTimeString();
            
            // Emit initial location
            this.$emit('location-update', this.currentLocation);
          },
          (error) => {
            // Handle location errors silently
            // error.code: 1 = permission denied, 2 = unavailable, 3 = timeout
          },
          geoOptions
        );

        // Watch position for real-time updates
        this.watchId = navigator.geolocation.watchPosition(
          (position) => {
            this.currentLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            this.locationAccuracy = Math.round(position.coords.accuracy);
            this.lastUpdate = new Date().toLocaleTimeString();
            
            // Emit location update to parent
            this.$emit('location-update', this.currentLocation);
          },
          (error) => {
            // Handle watch position errors silently
          },
          geoOptions
        );
      }
    },

    stopLocationTracking() {
      if (this.watchId) {
        navigator.geolocation.clearWatch(this.watchId);
        this.watchId = null;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.trip-map {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
}
</style>

